import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useToast } from "@chakra-ui/react";
import { useAuth } from "context";
import questionService from "api/issb/question.service";
import { issbKeys } from "hooks/query";
import { showErrorToast, USER_ROLES } from "utils";
import { foundationYearEditability } from "utils/foundationYearEditable";
import useAirUserRoles from "../../../../utils/AuthUtils";
import { useRightSidebar } from "../../../../context/RightSidebarContext";
import AnswerDetailManagement from "../../IssbDataInput/AnswerDetailManagement";

/** Roles holding the backend's write authority (`update:organization-questionnaires-data`). */
export const WRITE_ROLES = [USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MEMBER, "ESG_ADMIN"];

/**
 * One workstream's question: the thing the whole ISSB frame is reused for.
 *
 * Owner, assignees, activity, comments and the submit/approve transitions all come from existing components
 * driven by this question — v2 contributes no workflow code, only the question.
 *
 * Also registers the right sidebar, because the sidebar IS this question's management panel and having the
 * page do it separately would mean two places that have to agree on which question is in view.
 *
 * @param {string} questionId the question's businessId — what the ISSB frontend calls "id"
 * @param {React.ReactNode} [trayDetails] extra lines for the Manage tab (the workstream's entity and reporting
 *   period), shown after Assignees
 */
export default function useFoundationYearQuestion(questionId, trayDetails) {
  const { getAccessTokenSilently, userPermissions } = useAuth();
  const toast = useToast();
  const { setRightSidebar, doOpen } = useRightSidebar();
  const { isOwner, isAssigned } = useAirUserRoles();

  const { data: question, isLoading } = useQuery(
    [...issbKeys.answer(), questionId],
    () => getAccessTokenSilently().then(() => questionService.get(questionId)),
    {
      enabled: !!questionId,
      // cacheTime 0 so moving between workstreams never shows the previous one's stage for a frame.
      cacheTime: 0,
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    }
  );

  const hasWriteRole = useMemo(
    () => !!userPermissions?.some((role) => WRITE_ROLES.includes(role)),
    [userPermissions]
  );

  /*
    Both halves of the rule, in one place. The stage-and-assignment test alone is v1's, and it is not enough
    here: an ORG_VIEWER passes it and is then refused by the server, so the affordance has to be hidden rather
    than fail on click — an authority denial answers 401 in this app.
  */
  const { canEdit, reason: cannotEditReason } = useMemo(
    () => foundationYearEditability(question, { hasWriteRole, isOwner, isAssigned }),
    [question, hasWriteRole, isOwner, isAssigned]
  );

  useEffect(() => {
    if (!question) return;
    /*
      Registered as the page's DEFAULT sidebar (second arg true), so it is restored after
      another component borrows the panel — the same call shape v1 uses. Statuses and relevance are hidden:
      completeness and confidence are per-answer ISSB concepts with no meaning for a table of figures.
    */
    setRightSidebar(
      <AnswerDetailManagement
        questionData={question}
        canSave={canEdit}
        hideStatuses={true}
        hideRelevance={true}
        extraDetails={trayDetails}
      />,
      true,
      "AnswerDetailManagement",
      // Closable, with "Manage" in the header to reopen it — as on the disclosure page — and closed on entry
      // here, so the figures table gets the full width until someone asks for the panel.
      { closableDefault: true, label: "Manage", startClosed: true }
    );
    doOpen("AnswerDetailManagement");
  }, [question, canEdit, trayDetails, setRightSidebar, doOpen]);

  return { question, isLoading, canEdit, cannotEditReason, hasWriteRole };
}
